import type {
  ShopDto,
  TaskDto,
  TaskUnmatchedLineDto,
} from "@babackapp/private-api-client/types";
import useGetImportTaskFileUrl from "@babackapp/private-api-client/client/use-get-import-task-file-url";
import useTranslation from "@babackapp/translations/use-translation";
import SimpleMenu from "@babackapp/ui/components/simple-menu";
import {
  ArrowDown,
  DownloadIcon,
  MoreVertical,
  RefreshCwIcon,
} from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";
import ReplayTaskAlertDialog from "./replay-task-alert-dialog";

export default function Task(props: { task: TaskDto; shop: ShopDto }) {
  const t = useTranslation();
  const [detailOpen, setDetailOpen] = useState(false);
  const [isReplayDialogOpen, setIsReplayDialogOpen] = useState(false);

  const { refetch: fetchFileUrl } = useGetImportTaskFileUrl(
    props.shop.id,
    props.task.jobId,
    props.task.id,
    false,
  );

  const handleDownload = async () => {
    const { data } = await fetchFileUrl();
    if (data?.url) {
      window.open(data.url, "_blank");
    }
  };

  const hasFile = !!props.task.input.file?.name;
  const anomalyCount = (props.task.result?.unmatchedLines ?? []).filter(
    isAnomaly,
  ).length;

  return (
    <>
      <div className="flex text-sm space-x-4 text-gray-500 font-normal">
        <div className="">
          {format(props.task.createdAt, "yyyy-MM-dd HH:mm")}
        </div>
        <div className="flex-1">{props.task.input.file?.name}</div>
        <div className="flex items-center gap-2">
          {anomalyCount > 0 && (
            <div className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
              {t("settings.integrations.imports.task.unmatchedBadge", {
                count: anomalyCount,
              })}
            </div>
          )}
          <TaskStatus taskStatus={props.task.status} />
        </div>
        <div className="">
          <SimpleMenu
            iconTitle={<MoreVertical className="h-4 w-4" />}
            type={"neutral"}
            menu={[
              {
                onClick: () => setIsReplayDialogOpen(true),
                title: t("settings.integrations.imports.rerun"),
                icon: <RefreshCwIcon className="h-4 w-4 mr-1 text-gray-500" />,
              },
              ...(hasFile
                ? [
                    {
                      onClick: handleDownload,
                      title: t("settings.integrations.imports.downloadFile"),
                      icon: (
                        <DownloadIcon className="h-4 w-4 mr-1 text-gray-500" />
                      ),
                    },
                  ]
                : []),
              {
                onClick: () => {
                  setDetailOpen(true);
                },
                title: t("misc.seemore"),
                icon: <ArrowDown className="h-4 w-4 mr-1 text-gray-500" />,
              },
            ]}
          />
        </div>
      </div>

      {detailOpen && <TaskDetail task={props.task} />}

      <ReplayTaskAlertDialog
        task={props.task}
        shop={props.shop}
        isOpen={isReplayDialogOpen}
        onOpenChange={setIsReplayDialogOpen}
      />
    </>
  );
}

function TaskStatus(props: { taskStatus: string }) {
  const t = useTranslation();
  if (props.taskStatus == "done") {
    return (
      <div className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
        <div className="-ml-0.5 mr-1.5 h-1.5 w-1.5 bg-green-400 rounded-full"></div>
        {t("misc.ok").toLowerCase()}
      </div>
    );
  }
  // "failed": the queue gave up on the file (unreadable, crash).
  if (props.taskStatus == "error" || props.taskStatus == "failed") {
    return (
      <div className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
        <div className="-ml-0.5 mr-1.5 h-1.5 w-1.5 bg-red-400 rounded-full"></div>
        {t("misc.error").toLowerCase()}
      </div>
    );
  }
  if (props.taskStatus == "running") {
    return (
      <div className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800">
        <div className="-ml-0.5 mr-1.5 h-1.5 w-1.5 bg-orange-400 rounded-full"></div>
        {t("misc.running").toLowerCase()}
      </div>
    );
  }
  if (props.taskStatus == "pending") {
    return (
      <div className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800">
        <div className="-ml-0.5 mr-1.5 h-1.5 w-1.5 bg-orange-400 rounded-full"></div>
        {t("misc.pending").toLowerCase()}
      </div>
    );
  }
  return <></>;
}

function TaskDetail(props: { task: TaskDto }) {
  const t = useTranslation();
  const result = props.task.result;
  const unmatchedLines = result?.unmatchedLines ?? [];
  const failedReturnRequests = result?.failedReturnRequests ?? [];

  return (
    <div className="mt-1 space-y-2 rounded bg-gray-50 p-2 text-xs text-gray-600">
      {result?.error && <p className="text-red-700">{result.error}</p>}
      {result?.linesRead != null && (
        <p>
          {t("settings.integrations.imports.task.summary", {
            linesRead: result.linesRead,
            returnsTreated: result.linesTreated ?? 0,
          })}
        </p>
      )}
      {failedReturnRequests.length > 0 && (
        <p>
          {t("settings.integrations.imports.task.failedReturnRequests")}{" "}
          {failedReturnRequests.join(", ")}
        </p>
      )}
      {unmatchedLines.length > 0 && (
        <div className="space-y-1">
          <p className="font-medium text-amber-800">
            {t("settings.integrations.imports.task.unmatchedTitle")}
          </p>
          <table className="w-full text-left">
            <thead>
              <tr className="text-gray-500">
                <th className="pr-2 font-medium">
                  {t("settings.integrations.imports.task.columns.line")}
                </th>
                <th className="pr-2 font-medium">
                  {t("settings.integrations.imports.task.columns.order")}
                </th>
                <th className="pr-2 font-medium">
                  {t("settings.integrations.imports.task.columns.product")}
                </th>
                <th className="pr-2 font-medium">
                  {t("settings.integrations.imports.task.columns.quantity")}
                </th>
                <th className="font-medium">
                  {t("settings.integrations.imports.task.columns.reason")}
                </th>
              </tr>
            </thead>
            <tbody>
              {unmatchedLines.map((line, index) => (
                <tr
                  key={`${line.lineNumber}-${index}`}
                  className={isAnomaly(line) ? undefined : "text-gray-400"}
                >
                  <td className="pr-2">{line.lineNumber || "—"}</td>
                  <td className="pr-2 font-medium">{lineIdentifier(line)}</td>
                  <td className="pr-2">{line.sku || line.barcode || "—"}</td>
                  <td className="pr-2">{line.quantity}</td>
                  <td>
                    {t(
                      `settings.integrations.imports.task.reasons.${line.reason}`,
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Lines of returns already treated: a warehouse resending them, not an anomaly.
function isAnomaly(line: TaskUnmatchedLineDto): boolean {
  return line.reason !== "RETURN_REQUEST_ALREADY_TREATED";
}

function lineIdentifier(line: TaskUnmatchedLineDto): string {
  return line.orderName || line.orderId || line.returnCode || "—";
}
